import type {ReactElement} from 'react';
import React from 'react';
import {SubtitleEditingBox} from '@Components/poster-editor/components/poster-editor-subtitles-editor/components/subtitles-editor/subtitle-editing-box/subtitle-editing-box';
import {getUniqueString} from '@Utils/string.util';
import {insert} from '@Utils/array.util';
import {
  MergeSubtitleWith,
  SubtitlesEditorKeyDown,
} from '@Components/poster-editor/components/poster-editor-subtitles-editor/components/subtitles-editor/subtitle-editing-box/subtitle-editing-box.types';
import {Button, Size, Type} from '@Components/button';
import Divider from '@Components/divider';
import {AnimatePresence, motion} from 'framer-motion';
import type {WordObject} from '@PosterWhiteboard/items/transcript-item/subtitle/word/word.types';
import {getSubtitlesWordArrayAfterTextEdited} from '@Libraries/ai-transcript.library';
import type {SubtitlesEditorProps} from './subtitles-editor.types';
import styles from './subtitles-editor.module.scss';
import {deleteTranscriptItemWithSubtitleId} from '@PosterWhiteboard/page/page-items.class';

const MIN_TIME_STEP = 0.01;

export function SubtitlesEditor({...props}: SubtitlesEditorProps): ReactElement {
  const [autoFocus, setAutoFocus] = React.useState(false);

  const getNextSubtitleId = (currentSubtitleId: string): string | null => {
    const currentSubtitleIndex = props.subtitles.subtitlesOrder.indexOf(currentSubtitleId);
    if (currentSubtitleIndex === -1) {
      console.error(`Subtitle id ${currentSubtitleId} does not exist in subtitles.subtitlesOrder`);
      return null;
    }

    if (currentSubtitleIndex === props.subtitles.subtitlesOrder.length - 1) {
      return null;
    }

    return props.subtitles.subtitlesOrder[currentSubtitleIndex + 1];
  };

  const getPreviousSubtitleId = (currentSubtitleId: string): string | null => {
    const currentSubtitleIndex = props.subtitles.subtitlesOrder.indexOf(currentSubtitleId);
    if (currentSubtitleIndex === -1) {
      console.error(`Subtitle id ${currentSubtitleId} does not exist in subtitles.subtitlesOrder`);
      return null;
    }

    if (currentSubtitleIndex === 0) {
      return null;
    }

    return props.subtitles.subtitlesOrder[currentSubtitleIndex - 1];
  };

  const changeSubtitleText = (id: string, subtitleText: string, undoable = true): void => {
    if (subtitleText.length > 500 || subtitleText.split(/\s+/).length > 100) {
      console.warn('Subtitle text exceeds the limit of 500 characters or 100 words.');
      return;
    }

    const subtitles = {...props.subtitles};
    subtitles.subtitlesHashmap[id].text = subtitleText;
    subtitles.subtitlesHashmap[id].hasUserEdited = true;

    subtitles.subtitlesHashmap[id].words = getSubtitlesWordArrayAfterTextEdited(subtitleText, subtitles.subtitlesHashmap[id].startTime, subtitles.subtitlesHashmap[id].endTime);

    void props.onSubtitlesChange(subtitles, undoable);
  };

  const deleteSubtitle = async (idToDelete: string): Promise<void> => {
    const subtitles = {...props.subtitles};

    const prevSubtitleId = getPreviousSubtitleId(idToDelete);
    const nextSubtitleId = getNextSubtitleId(idToDelete);

    if (Object.keys(subtitles.subtitlesHashmap).length === 1) {
      deleteTranscriptItemWithSubtitleId(idToDelete);
      return;
    }

    delete subtitles.subtitlesHashmap[idToDelete];
    const newSubtitlesOrder = subtitles.subtitlesOrder.filter((subtitleId) => {
      return subtitleId !== idToDelete;
    });
    subtitles.subtitlesOrder = [...newSubtitlesOrder];

    await props.onSubtitlesChange(subtitles);
    if (prevSubtitleId) {
      changeActiveSubtitle(prevSubtitleId);
    } else if (nextSubtitleId) {
      changeActiveSubtitle(nextSubtitleId);
    }
  };

  const selectNextSubtitle = (currentSubtitleId: string): void => {
    const nextSubtitleId = getNextSubtitleId(currentSubtitleId);
    if (!nextSubtitleId) return;

    changeActiveSubtitle(nextSubtitleId);
  };

  const selectPreviousSubtitle = (currentSubtitleId: string): void => {
    const prevSubtitleId = getPreviousSubtitleId(currentSubtitleId);
    if (!prevSubtitleId) return;

    changeActiveSubtitle(prevSubtitleId);
  };

  const changeActiveSubtitle = (id: string): void => {
    if (props.selectedSubtitleId !== id) {
      if (props.selectedSubtitleId && props.subtitles.subtitlesHashmap[props.selectedSubtitleId] && props.subtitles.subtitlesHashmap[props.selectedSubtitleId].text === '') {
        void deleteSubtitle(props.selectedSubtitleId);
      }

      props.setSelectedSubtitleId(id);
    }
  };

  const mergeSubtitleWithPrevious = (idOfSubtitleToMergeWithPrevious: string): void => {
    const currentSubtitle = props.subtitles.subtitlesHashmap[idOfSubtitleToMergeWithPrevious];

    const prevSubtitleId = getPreviousSubtitleId(idOfSubtitleToMergeWithPrevious);
    if (!prevSubtitleId) return;

    const mergedText = `${props.subtitles.subtitlesHashmap[prevSubtitleId].text} ${currentSubtitle.text}`;
    const mergedSubtitleEndTime = currentSubtitle.endTime;

    const subtitles = {...props.subtitles};

    subtitles.subtitlesHashmap[prevSubtitleId].text = mergedText;
    subtitles.subtitlesHashmap[prevSubtitleId].endTime = mergedSubtitleEndTime;
    subtitles.subtitlesHashmap[prevSubtitleId].words = [...subtitles.subtitlesHashmap[prevSubtitleId].words, ...currentSubtitle.words];

    delete subtitles.subtitlesHashmap[idOfSubtitleToMergeWithPrevious];
    const newSubtitlesOrder = subtitles.subtitlesOrder.filter((subtitleId) => {
      return subtitleId !== idOfSubtitleToMergeWithPrevious;
    });
    subtitles.subtitlesOrder = [...newSubtitlesOrder];

    void props.onSubtitlesChange(subtitles);
    props.setSelectedSubtitleId(prevSubtitleId);
  };

  const mergeSubtitleWithNext = (idOfSubtitleToMergeWithNext: string): void => {
    const currentSubtitle = {...props.subtitles.subtitlesHashmap[idOfSubtitleToMergeWithNext]};

    const nextSubtitleId = getNextSubtitleId(idOfSubtitleToMergeWithNext);
    if (!nextSubtitleId) return;

    const mergedText = `${currentSubtitle.text} ${props.subtitles.subtitlesHashmap[nextSubtitleId].text}`;
    const mergedSubtitleStartTime = currentSubtitle.startTime;

    const subtitles = {...props.subtitles};

    subtitles.subtitlesHashmap[nextSubtitleId].text = mergedText;
    subtitles.subtitlesHashmap[nextSubtitleId].startTime = mergedSubtitleStartTime;
    subtitles.subtitlesHashmap[nextSubtitleId].words = [...currentSubtitle.words, ...subtitles.subtitlesHashmap[nextSubtitleId].words];

    delete subtitles.subtitlesHashmap[idOfSubtitleToMergeWithNext];

    const newSubtitlesOrder = subtitles.subtitlesOrder.filter((subtitleId) => {
      return subtitleId !== idOfSubtitleToMergeWithNext;
    });
    subtitles.subtitlesOrder = [...newSubtitlesOrder];

    void props.onSubtitlesChange(subtitles);
    props.setSelectedSubtitleId(nextSubtitleId);
  };

  const getSplitSubtitleWords = (currentSubtitleId: string, caretPosition: number): WordObject[][] | undefined => {
    const subtitleToSplit = props.subtitles.subtitlesHashmap[currentSubtitleId];

    const wordsToSplit = subtitleToSplit.words;

    const subtitleTextLength = subtitleToSplit.text.length;

    if (!caretPosition || caretPosition === subtitleTextLength || caretPosition === 0) {
      return undefined;
    }

    const lastWordMatch = /\S+$/.exec(subtitleToSplit.text.trim());
    const lastWordStart = lastWordMatch ? subtitleToSplit.text.lastIndexOf(lastWordMatch[0]) : -1;

    if (caretPosition > lastWordStart) {
      return undefined;
    }

    const splitText = getSplitSubtitleText(currentSubtitleId, caretPosition);
    if (!splitText) {
      return undefined;
    }

    const textToRetainInCurrentSubtitle = splitText[0];

    const splitTextToRetainInCurrentSubtitle = textToRetainInCurrentSubtitle.split(' ');
    const wordsToRetainInCurrentSubtitle = wordsToSplit.slice(0, splitTextToRetainInCurrentSubtitle.length);

    const wordsToMoveToNewSubtitle = wordsToSplit.slice(splitTextToRetainInCurrentSubtitle.length, wordsToSplit.length);

    return [wordsToRetainInCurrentSubtitle, wordsToMoveToNewSubtitle];
  };

  const getSplitSubtitleText = (currentSubtitleId: string, caretPosition: number): string[] | undefined => {
    const subtitleToSplit = props.subtitles.subtitlesHashmap[currentSubtitleId];

    const subtitleTextLength = subtitleToSplit.text.length;

    if (!caretPosition || caretPosition === 0) {
      return undefined;
    }

    const lastWordMatch = /\S+$/.exec(subtitleToSplit.text.trim());
    const lastWordStart = lastWordMatch ? subtitleToSplit.text.lastIndexOf(lastWordMatch[0]) : -1;

    if (caretPosition > lastWordStart) {
      return undefined;
    }

    // determine the index to split
    // if the current char, or the previous char, is a space, then we split at caretPosition
    // otherwise, we go to the end of the current word where the caret is, and split there
    let indexToStartSplit = caretPosition;
    if (subtitleToSplit.text[caretPosition] !== ' ' && subtitleToSplit.text[caretPosition - 1] !== ' ') {
      const nextSpaceIndex = subtitleToSplit.text.indexOf(' ', caretPosition);
      indexToStartSplit = nextSpaceIndex === -1 ? subtitleTextLength : nextSpaceIndex;
    }

    const textToRetainInCurrentSubtitle = subtitleToSplit.text.substring(0, indexToStartSplit);
    const textToMoveToNewSubtitle = subtitleToSplit.text.substring(indexToStartSplit);

    if (!textToRetainInCurrentSubtitle.trim().length || !textToMoveToNewSubtitle.trim().length) {
      return undefined;
    }

    return [textToRetainInCurrentSubtitle.trim(), textToMoveToNewSubtitle.trim()];
  };

  const splitSubtitle = async (idOfSubtitleToSplit: string, caretPosition?: number): Promise<void> => {
    if (!caretPosition) {
      return;
    }

    if (Object.values(props.subtitles.subtitlesHashmap).length >= 1000) {
      return;
    }

    const splitSubtitleText = getSplitSubtitleText(idOfSubtitleToSplit, caretPosition);
    const splitSubtitleWords = getSplitSubtitleWords(idOfSubtitleToSplit, caretPosition);

    if (!splitSubtitleText) {
      return;
    }

    if (!splitSubtitleWords) {
      return;
    }

    const [textBeforeSplit, textAfterSplit] = splitSubtitleText;
    const [wordsBeforeSplit, wordsAfterSplit] = splitSubtitleWords;

    const subtitles = {...props.subtitles};

    const subtitleToSplit = subtitles.subtitlesHashmap[idOfSubtitleToSplit];

    const {endTime} = subtitleToSplit;

    const newSubtitleId = getUniqueString();
    const newEndTimeOfSubtitleToSplit = wordsBeforeSplit[wordsBeforeSplit.length - 1].endTime;

    subtitles.subtitlesHashmap[idOfSubtitleToSplit] = {
      ...subtitleToSplit,
      text: textBeforeSplit,
      startTime: subtitleToSplit.startTime,
      endTime: newEndTimeOfSubtitleToSplit,
      words: wordsBeforeSplit,
    };

    subtitles.subtitlesHashmap[newSubtitleId] = {
      ...subtitleToSplit,
      subtitleUID: newSubtitleId,
      text: textAfterSplit,
      startTime: wordsAfterSplit[0].startTime,
      endTime,
      words: wordsAfterSplit,
    };

    const index = subtitles.subtitlesOrder.indexOf(idOfSubtitleToSplit);
    const subtitlesOrder = insert(subtitles.subtitlesOrder, index + 1, newSubtitleId);
    subtitles.subtitlesOrder = [...subtitlesOrder];

    await props.onSubtitlesChange(subtitles);
    changeActiveSubtitle(newSubtitleId);
  };

  const addSubtitleAfter = async (idOfSubtitleToAddAfter: string): Promise<void> => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return;
    }

    if (Object.values(props.subtitles.subtitlesHashmap).length >= 1000) {
      return;
    }

    if (isAddAfterSubtitleDisabled(idOfSubtitleToAddAfter)) {
      return;
    }

    const newSubtitleId = getUniqueString();

    const subtitles = {...props.subtitles};

    const isLastSubtitle = props.subtitles.subtitlesOrder.indexOf(idOfSubtitleToAddAfter) === props.subtitles.subtitlesOrder.length - 1;

    if (isLastSubtitle) {
      const newSubtitleStartTime = props.subtitles.subtitlesHashmap[idOfSubtitleToAddAfter].endTime + MIN_TIME_STEP;

      subtitles.subtitlesHashmap[newSubtitleId] = {
        ...props.subtitles.subtitlesHashmap[idOfSubtitleToAddAfter],
        subtitleUID: newSubtitleId,
        startTime: newSubtitleStartTime,
        endTime: newSubtitleStartTime + 3,
        text: '',
        hasUserEdited: true,
      };

      const subtitlesOrder = [...subtitles.subtitlesOrder];
      subtitlesOrder.push(newSubtitleId);
      subtitles.subtitlesOrder = [...subtitlesOrder];

      await props.onSubtitlesChange(subtitles);
      changeActiveSubtitle(newSubtitleId);
      setAutoFocus(true);
    } else {
      const nextSubtitleId = getNextSubtitleId(idOfSubtitleToAddAfter);
      if (!nextSubtitleId) {
        return;
      }

      const newSubtitleEndTime = props.subtitles.subtitlesHashmap[nextSubtitleId].startTime;

      subtitles.subtitlesHashmap[newSubtitleId] = {
        ...props.subtitles.subtitlesHashmap[idOfSubtitleToAddAfter],
        subtitleUID: newSubtitleId,
        startTime: props.subtitles.subtitlesHashmap[idOfSubtitleToAddAfter].endTime + MIN_TIME_STEP,
        endTime: newSubtitleEndTime - MIN_TIME_STEP,
        text: '',
        hasUserEdited: true,
      };

      const index = subtitles.subtitlesOrder.indexOf(idOfSubtitleToAddAfter);
      const subtitlesOrder = insert(subtitles.subtitlesOrder, index + 1, newSubtitleId);
      subtitles.subtitlesOrder = [...subtitlesOrder];

      await props.onSubtitlesChange(subtitles);
      changeActiveSubtitle(newSubtitleId);
      setAutoFocus(true);
    }
  };

  const isAddAfterSubtitleDisabled = (subtitleId: string): boolean => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return false;
    }

    if (props.subtitles.subtitlesHashmap[subtitleId].endTime + 3 >= 600) {
      return true;
    }

    if (Object.values(props.subtitles.subtitlesHashmap).length >= 1000) {
      return true;
    }

    const currentSubtitleEndTime = props.subtitles.subtitlesHashmap[subtitleId].endTime;
    const nextSubtitleId = getNextSubtitleId(subtitleId);
    if (!nextSubtitleId) {
      return false;
    }

    const nextSubtitleStartTime = props.subtitles.subtitlesHashmap[nextSubtitleId].startTime;

    return Math.abs(nextSubtitleStartTime - currentSubtitleEndTime) <= 1;
  };

  const handleKeyDown = (currentSubtitleId: string, key: SubtitlesEditorKeyDown, caretPosition?: number): void => {
    if (key === SubtitlesEditorKeyDown.DOWN) {
      selectNextSubtitle(currentSubtitleId);
    } else if (key === SubtitlesEditorKeyDown.UP) {
      selectPreviousSubtitle(currentSubtitleId);
    } else if (key === SubtitlesEditorKeyDown.ENTER) {
      const lastWordMatch = /\S+$/.exec(props.subtitles.subtitlesHashmap[currentSubtitleId].text.trim());
      const lastWordStart = lastWordMatch ? props.subtitles.subtitlesHashmap[currentSubtitleId].text.lastIndexOf(lastWordMatch[0]) : -1;
      const lastWordEnd = lastWordMatch ? lastWordStart + lastWordMatch[0].length : -1;

      if (caretPosition && caretPosition > lastWordStart && caretPosition >= lastWordEnd) {
        void addSubtitleAfter(currentSubtitleId);
        return;
      }

      void splitSubtitle(currentSubtitleId, caretPosition);
    } else if (key === SubtitlesEditorKeyDown.BACKSPACE) {
      mergeSubtitleWithPrevious(currentSubtitleId);
    }

    setAutoFocus(true);
  };

  const mergeSubtitles = (currentSubtitleId: string, mergeWith: MergeSubtitleWith): void => {
    if (mergeWith === MergeSubtitleWith.NEXT) {
      mergeSubtitleWithNext(currentSubtitleId);
    } else if (mergeWith === MergeSubtitleWith.PREVIOUS) {
      mergeSubtitleWithPrevious(currentSubtitleId);
    }
  };

  const onPlayPauseSubtitle = (currentSubtitleId: string): void => {
    void window.posterEditor?.whiteboard?.pauseOrPlayBetweenStartAndEndTime(
      props.subtitles.subtitlesHashmap[currentSubtitleId].startTime,
      props.subtitles.subtitlesHashmap[currentSubtitleId].endTime
    );
  };

  return (
    <div className="flex-v-row _fit-height _full-width">
      <AnimatePresence initial={false}>
        {Object.values(props.subtitles.subtitlesOrder).map((subtitleId, index) => {
          return (
            <div key={subtitleId}>
              <motion.div
                initial={{
                  height: 0,
                }}
                animate={{
                  height: 'fit-content',
                  overflow: 'hidden',
                }}
                exit={{
                  height: 0,
                  overflow: 'hidden',
                }}
                transition={{duration: 0.2}}
              >
                <SubtitleEditingBox
                  disableMergeWithPreviousOption={index === 0}
                  disableMergeWithNextOption={index === Object.values(props.subtitles.subtitlesOrder).length - 1}
                  onPlayPauseSubtitle={onPlayPauseSubtitle}
                  autoFocus={autoFocus}
                  setAutoFocus={setAutoFocus}
                  isMobileVariant={props.isMobileVariant}
                  onMergeSubtitles={mergeSubtitles}
                  onKeyDown={handleKeyDown}
                  onAddSubtitleAfter={addSubtitleAfter}
                  disableAddAfter={isAddAfterSubtitleDisabled(subtitleId)}
                  onSubtitleBoxFocussed={changeActiveSubtitle}
                  isSelected={props.selectedSubtitleId === subtitleId}
                  subtitleId={subtitleId}
                  key={subtitleId}
                  text={props.subtitles.subtitlesHashmap[subtitleId].text}
                  startTime={props.subtitles.subtitlesHashmap[subtitleId].startTime}
                  endTime={props.subtitles.subtitlesHashmap[subtitleId].endTime}
                  onSubtitleTextChange={changeSubtitleText}
                  onSubtitleDelete={deleteSubtitle}
                />
              </motion.div>
              <div className={`spacing-p-2 _cursor-pointer fit-content flex-center ${styles.dividerContainer}`}>
                <div className={`_invisible flexbox ${styles.actionButtonsWrapper}`}>
                  <Button
                    disabled={index === props.subtitles.subtitlesOrder.length - 1}
                    onClick={() => {
                      mergeSubtitles(subtitleId, MergeSubtitleWith.NEXT);
                    }}
                    customClasses="spacing-m-r-2"
                    icon="icon-merge-subtitles"
                    text={window.i18next.t('pmwjs_merge')}
                    size={Size.XSMALL}
                    type={Type.SECONDARY}
                  />
                  <Button
                    disabled={isAddAfterSubtitleDisabled(subtitleId)}
                    onClick={() => {
                      addSubtitleAfter(subtitleId);
                    }}
                    icon="icon-plus"
                    size={Size.XSMALL}
                    type={Type.SECONDARY}
                  />
                </div>
                <Divider additionalClasses={`_invisible ${styles.divider}`} key={subtitleId} />
              </div>
            </div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
